import React from 'react';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IFilter, IOrderRecallDialogProperties } from './OrderRecallDialog.properties';
import { OrderRecallStyles } from './OrderRecallDialog.styles';
import { withStyles } from '@material-ui/core/styles';
import DataGrid from 'devextreme-react/data-grid';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import FilterRow from 'components/common/DataGridDevEx/FilterRow';
import filterRow from './OrderRecallDialogContent.filterRow';
import { SUBTITLE, columns } from './OrderRecallDialog.constants';
import { IOutstandingOrdersFilter } from 'api/swaggerTypes';
import { useCreateLotSale, useRecallOrder, useSearchOutstandingCustomerOrders } from 'api/Worksale/worksaleLatest';
import { createDataSource } from 'components/common/DataGridDevEx/DataGrid.hooks';
import { isEqual } from 'lodash';
import { isScreenRequest } from 'api/utils';
import { useLostSale } from '../Worksale.hooks';
import { IFormViewModelHandle } from 'components/common/Modals/FormViewModal.properties';
import { HotKeys } from 'react-hotkeys';
import { useMemoizedKeyHandlers } from 'utils/hooks';
import { useProcessValidationForms } from 'utils/processValidationForms';
import { useSPContextSelector } from '../Worksale.context';
import ToggleButton from 'components/common/ToggleButton';
import { isNull } from 'utils/utils';
import { ENTER_KEY, ESCAPE, TAB } from 'utils/constants';
import Selectors from '../Worksale.selectors';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';

const DELAY = 10;

const getActions = (onClose, recallOrderCallback, newCallback, RecallOrNewOrder: boolean, gridLength: number, isRowSelected: boolean, recallButtonRef, cancelButtonRef) => {
    if (RecallOrNewOrder) {
        return [
            {
                title: 'New',
                iconName: 'CheckCircle',
                isDefault: (gridLength > 0),
                disabled: (gridLength === 0) || recallButtonRef?.current?.isSubmitting,
                listener: () => {
                    if (newCallback) {
                        newCallback();
                    }
                }
            },
            {
                title: 'Recall',
                iconName: 'CheckCircle',
                disabled: (gridLength === 0) || !isRowSelected || recallButtonRef?.current?.isSubmitting,
                ref: recallButtonRef,
                listener: () => {
                    if (recallOrderCallback) {
                        recallOrderCallback();
                    }
                }
            },
            {
                title: 'Cancel',
                iconName: 'Cancel',
                ref: cancelButtonRef,
                listener: () => {
                    if (onClose) {
                        onClose();
                    }
                },
            },
        ];
    }

    return [
        {
            title: 'Recall',
            iconName: 'CheckCircle',
            isDefault: (gridLength > 0),
            disabled: (gridLength === 0) || recallButtonRef?.current?.isSubmitting,
            ref: recallButtonRef,
            listener: () => {
                if (recallOrderCallback) {
                    recallOrderCallback();
                }
            }
        },
        {
            title: 'Cancel',
            iconName: 'Cancel',
            ref: cancelButtonRef,
            listener: () => {
                if (onClose) {
                    onClose();
                }
            },
        },
    ];
};

const OrderRecallDialog = (props: IOrderRecallDialogProperties) => {
    const {
        classes, open, loading, customerId, CustomerPurchaseOrder: customerPO,
        salesEntity, date, period, recallOrNewOrder, onClose, onRecallSuccess, createWorkSale, dialogStyles
    } = props;

    const gridRef = React.useRef<DataGrid>();
    const formViewModalRef = React.useRef<IFormViewModelHandle>();
    const filterButtonRef = React.useRef<HTMLButtonElement>();

    const autoSupplyQuantity = useSPContextSelector<'autoSupplyQuantity'>((state) => state.autoSupplyQuantity);
    const disableAutoSupplyQuantity = useSPContextSelector<'disableAutoSupplyQuantity'>((state) => state.disableAutoSupplyQuantity);
    const worksaleId = useSPContextSelector(Selectors.WorksaleId);
    const unAppliedCustomerId = useSPContextSelector<'UnAppliedCustomerId'>((state) => state.UnAppliedCustomerId);
    const unAppliedCustomerPurchaseOrder = useSPContextSelector<'UnAppliedCustomerPurchaseOrder'>((state) => state.UnAppliedCustomerPurchaseOrder);

    const searchOrdersMutation = useSearchOutstandingCustomerOrders();
    const recallOrderMutation = useRecallOrder();
    const createLotSaleMutation = useCreateLotSale();
    const processValidationForms = useProcessValidationForms();

    const { setDialogState: setLostSaleDialogStates, closeModal: closeLostSaleDialog } = useLostSale();

    const [defaultSuppliedQuantityToggle, setDefaultSuppliedQuantityToggle] = React.useState<boolean>(autoSupplyQuantity);
    const [filters, setFilters] = React.useState<IFilter>({
        SalesEntity: null,
        OrderStatus: 'a',
        CustomerPurchaseOrder: '',
        DefaultEntity: false,
        Category: null,
        SalesOrder: null
    });
    const [filtersValuesSchema, setFiltersValuesSchema] = React.useState<any>({});
    const [selectedRow, setSelectedRow] = React.useState(null);
    const [gridLength, setGridLength] = React.useState(0);
    const [defaultFirstRowSelection, setDefaultFirstRowSelection] = React.useState<boolean>(false);
    const recallButtonRef = React.useRef<HTMLButtonElement & { isSubmitting?: boolean }>();
    const cancelButtonRef = React.useRef<HTMLButtonElement>();
    const filterRef = React.useRef({ isOpen: false });
    const lastToggleButtonRef = React.useRef<HTMLInputElement>();

    React.useEffect(
        () => {
            setDefaultFirstRowSelection(true);
        },
        []
    );

    React.useEffect(
        () => {
            if (recallOrNewOrder) {
                setFilters((previousFilters) => ({ ...previousFilters, CustomerPurchaseOrder: recallOrNewOrder ? customerPO : '', CustomerId: customerId, }));
            }
        },
        [recallOrNewOrder, customerPO, customerId]
    );

    React.useEffect(
        () => {
            if (worksaleId) {
                setFilters((previousFilters) => ({ ...previousFilters, CustomerId: customerId, }));
            }
        },
        [worksaleId]
    );
    React.useEffect(
        () => {
            if (!unAppliedCustomerId && !unAppliedCustomerPurchaseOrder) return;

            setFilters((previousFilters) => ({
                ...previousFilters,
                ...(unAppliedCustomerId && { CustomerId: unAppliedCustomerId }),
                ...(unAppliedCustomerPurchaseOrder && { CustomerPurchaseOrder: unAppliedCustomerPurchaseOrder }),
            }));
        },
        [unAppliedCustomerId, unAppliedCustomerPurchaseOrder]
    );

    React.useEffect(
        () => {
            setGridLength(gridRef.current?.instance?.getDataSource()?.items().length);
        },
        [gridRef.current?.instance?.getDataSource()?.items().length]);

    const setFiltersCallback = React.useCallback((_filters: IFilter) => setFilters({ ..._filters, DefaultSearch: false }), []);

    const searchOrdersApiCallback = React.useCallback(
        async (BatchPage, BatchSize, Sort) => {
            const { DefaultEntity, ...restFilters } = filters;
            const response = await searchOrdersMutation.mutateAsync({
                ...restFilters,
                urlQueryParams: {
                    DefaultEntity: DefaultEntity,
                    WorksaleId: worksaleId,
                    Sort,
                    BatchPage,
                    BatchSize
                }
            });

            const filterFromResponse = response?.OutstandingOrders?.inlineObject?.OutstandingOrdersFilter ?? {};
            const { SalesEntity, CustomerId, Category, SalesOrder, CustomerPurchaseOrder, OrderStatus } = filterFromResponse;

            const newFilter = {
                SalesEntity: SalesEntity || '',
                CustomerId: CustomerId || null,
                Category: Category || '',
                OrderStatus: OrderStatus || '',
                SalesOrder: SalesOrder || '',
                CustomerPurchaseOrder: CustomerPurchaseOrder || '',
                DefaultEntity: filters.DefaultEntity,
                DefaultSearch: false
            };

            if (!isEqual(newFilter, filters)) {
                setFiltersCallback(newFilter);
                const filtersSchema = response?.OutstandingOrders?.schema?.OutstandingOrdersFilter as any ?? {};
                setFiltersValuesSchema({
                    ...filtersSchema,
                    CustomerId: {
                        ...filtersSchema?.CustomerId,
                        ReadOnly: Boolean(worksaleId)
                    }
                });
            }

            return response?.OutstandingOrders?.inlineObject?.OutstandingCustomerOrder ?? [];
        },
        [worksaleId, filters]
    );

    const dataSource = React.useMemo(
        () => createDataSource(
            'SalesOrder',
            { fetch: searchOrdersApiCallback },
        ),
        [searchOrdersApiCallback]
    );

    const submitLostSale = React.useCallback(
        async (data) => {
            const { FromLineNumber, ...restData } = data;
            const response = await createLotSaleMutation.mutateAsync({ ...restData, urlQueryParams: { FromLineNumber } });

            if (response.Status && onClose && onRecallSuccess && closeLostSaleDialog) {
                closeLostSaleDialog();
                onClose();
                onRecallSuccess();
            }
        },
        [onclose, onRecallSuccess, closeLostSaleDialog]
    );

    const recallOrderApiCallback = React.useCallback(
        async (additionalData = {}) => {
            if (!selectedRow?.SalesOrder) {
                return;
            }

            recallButtonRef.current.isSubmitting = true;
            const response = await recallOrderMutation.mutateAsync({
                SalesOrder: selectedRow.SalesOrder,
                AutoSupplyQuantity: defaultSuppliedQuantityToggle,
                ...additionalData,
            });

            recallButtonRef.current.isSubmitting = false;

            if (response?.Forms) {
                await processValidationForms(response, {}, (ref) => recallOrderApiCallback({ ...ref, FromLineNumber: response?.RecallOrder?.FromLineNumber })).catch((err) => { console.warn(err); });
            }

            if (response.Status && isScreenRequest(response.Forms) && response.Forms?.[0]?.FormId === 'LostSale') {
                setLostSaleDialogStates({
                    open: true,
                    data: {
                        ...response.RecallOrder?.Lostsale,
                        FromLineNumber: response.RecallOrder.FromLineNumber,
                        WorksaleId: response.RecallOrder.WorksaleId
                    },
                    onOk: submitLostSale
                });
            }

            if (response.Status && onClose && onRecallSuccess) {
                onClose();
                onRecallSuccess();
            }
        },
        [defaultSuppliedQuantityToggle, selectedRow, recallOrderMutation?.data, onClose, onRecallSuccess, submitLostSale, setLostSaleDialogStates]
    );

    const newOrderApiCallback = React.useCallback(
        async () => {
            if (createWorkSale) {
                recallButtonRef.current.isSubmitting = true;
                await createWorkSale({
                    CustomerId: customerId,
                    CustomerPurchaseOrder: customerPO,
                    SalesEntity: salesEntity,
                    CheckCustomerPO: false,
                    Date: date,
                    Period: period,
                });
                recallButtonRef.current.isSubmitting = false;

                if (onClose) {
                    onClose();
                }
            }
        },
        [salesEntity, date, period, salesEntity, customerId, customerPO, createWorkSale, onClose]
    );

    const actions = React.useMemo(
        () => getActions(onClose, recallOrderApiCallback, newOrderApiCallback, recallOrNewOrder, gridLength, !isNull(selectedRow), recallButtonRef, cancelButtonRef),
        [recallOrNewOrder, recallOrderApiCallback, onClose, gridLength, newOrderApiCallback]
    );

    const handleToggleChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setDefaultSuppliedQuantityToggle(event.target.checked);
        },
        []
    );

    const focusAndSelectFirstRowGrid = React.useCallback(
        () => {
            if (!gridRef.current) {
                return;
            }

            gridRef.current?.instance?.getScrollable()?.scrollTo(0);
            gridRef.current?.instance.focus();

            if (gridRef.current?.instance?.getDataSource()?.items()?.length > 0) {
                gridRef.current?.instance?.selectRowsByIndexes([0]);
            } else {
                focusFilterButton();
            }
            setDefaultFirstRowSelection(false);
        },
        [gridRef.current, setDefaultFirstRowSelection]
    );

    const filterOnApply = ({ filters: appliedFilters }: { formName?: string; filters: IOutstandingOrdersFilter }) => {
        setFiltersCallback({ ...appliedFilters, DefaultEntity: false });
        setDefaultFirstRowSelection(true);
    };

    const handleOnSelectionChanged = React.useCallback(
        (e) => {
            const selectedRowData = e.selectedRowsData?.[0] ?? {};
            setSelectedRow(selectedRowData);
        },
        [setSelectedRow]
    );

    const onEscape = React.useCallback(
        () => {
            if (onClose) {
                onClose();
            }
        },
        [onClose]
    );

    const onEnter = React.useCallback(
        () => {
            if (recallButtonRef.current &&
                gridRef.current.instance?.$element()?.[0]?.contains(document.activeElement)) {
                recallOrderApiCallback();
            }
        },
        [recallButtonRef.current, recallOrderApiCallback]
    );

    const focusFilterButton = React.useCallback(
        () => {
            setTimeout(() => filterButtonRef?.current?.focus(), DELAY);
        },
        [filterButtonRef.current]
    );

    const handleGridKeyDown = React.useCallback(
        (e) => {
            const keyboardEvent = e.event as KeyboardEvent;
            const dataGridInstance = gridRef.current?.instance;

            if (!dataGridInstance) return;

            const currentRowIndex = dataGridInstance.option('focusedRowIndex');
            const currentColumnIndex = dataGridInstance.option('focusedColumnIndex');

            if (currentRowIndex === undefined || currentColumnIndex === undefined) return;

            const COLUMNS_COUNT = 10;

            const focusAndScrollToCell = (rowIndex, columnIndex) => {
                const cellElement = dataGridInstance.getCellElement(rowIndex, columnIndex);
                if (cellElement) {
                    dataGridInstance.option('focusedColumnIndex', columnIndex);
                    dataGridInstance.focus(cellElement);
                    dataGridInstance.getScrollable().scrollToElement(cellElement);
                }
            };

            if (keyboardEvent.key === 'ArrowRight') {
                keyboardEvent.preventDefault();
                const nextColumnIndex = currentColumnIndex + 1;
                if (nextColumnIndex < dataGridInstance.columnCount()) {
                    focusAndScrollToCell(currentRowIndex, nextColumnIndex);
                }

                return;
            }

            if (keyboardEvent.key === 'ArrowLeft') {
                keyboardEvent.preventDefault();
                const previousColumnIndex = currentColumnIndex - 1;

                if (previousColumnIndex >= 0) {
                    if (previousColumnIndex < COLUMNS_COUNT) {
                        dataGridInstance.getScrollable().scrollTo({ x: 0 });
                    }
                    focusAndScrollToCell(currentRowIndex, previousColumnIndex);
                }

                return;
            }

            if (keyboardEvent.keyCode === TAB && keyboardEvent.shiftKey) {
                if (currentColumnIndex <= COLUMNS_COUNT) {
                    keyboardEvent.preventDefault();
                    dataGridInstance.getScrollable().scrollTo({ x: 0 });
                    focusFilterButton();

                    return;
                }
                setTimeout(focusFilterButton, 0);

                return;
            }

            if (keyboardEvent.keyCode === TAB && !keyboardEvent.shiftKey) {
                handleFocusToToggle();

                return;
            }

            if (keyboardEvent.keyCode === ESCAPE) {
                onEscape();

                return;
            }

            if (keyboardEvent.keyCode === ENTER_KEY) {
                onEnter();

                return;
            }
        },
        [onEscape, onEnter, formViewModalRef, focusFilterButton, gridRef]
    );

    const handleOnContentReady = React.useCallback(
        () => {
            if (defaultFirstRowSelection) {
                focusAndSelectFirstRowGrid();
            }
        },
        [defaultFirstRowSelection, focusAndSelectFirstRowGrid]
    );

    const handlers = useMemoizedKeyHandlers({
        Escape: onEscape,
        Enter: onEnter
    });

    const handleFilterButtonShiftTab = React.useCallback(
        () => {
            if (gridRef.current?.instance?.getDataSource()?.items()?.length > 0) {
                formViewModalRef.current?.focusLastButton();
            } else {
                formViewModalRef.current?.focusFirstButton();
            }

        },
        [formViewModalRef.current]
    );

    const handleFilterButtonTab = React.useCallback(
        () => {
            if (gridRef.current?.instance?.getDataSource()?.items()?.length > 0) {
                gridRef.current?.instance.focus();
            } else {
                handleFocusToToggle();
            }

        },
        [gridRef.current]
    );

    const handleFocusToToggle = React.useCallback(
        () => {
            setTimeout(() => lastToggleButtonRef.current?.focus(), 0);
        },
        [lastToggleButtonRef.current]
    );

    const handleToggleOnKeyDown = React.useCallback(
        (e) => {
            if (e.keyCode === TAB && e.shiftKey) {
                if ((gridRef.current?.instance?.getDataSource()?.items()?.length <= 0)) {
                    e.preventDefault();
                    focusFilterButton();
                }
            }
        },
        [gridRef.current, focusFilterButton]
    );

    return (
        <div>
            <HotKeys handlers={handlers}>
                <FormViewModal
                    open={open}
                    title='Recall Customer Order'
                    subTitle={recallOrNewOrder ? SUBTITLE : ''}
                    loading={loading}
                    innerRef={formViewModalRef}
                    onActionsTabOut={focusFilterButton}
                    onActionShiftTabOut={handleFocusToToggle}
                    filterRef={filterRef}
                    dialogStyles={dialogStyles}
                    modalContent={
                        <div className={classes.OrderRecallContainer}>
                            <FilterRow
                                filterButtonRef={filterButtonRef}
                                disableInitialApplyCall={true}
                                parameters={filterRow.parameters}
                                formName={filterRow.formName}
                                onApplyFilters={filterOnApply}
                                initialValues={{ ...filters }}
                                valuesSchema={filtersValuesSchema}
                                validate={filterRow.validate}
                                rowModelType={filterRow.rowModelType}
                                filterRef={filterRef}
                                onFilterButtonShiftTab={handleFilterButtonShiftTab}
                                onFilterButtonTab={handleFilterButtonTab}
                            />
                            <DynamicDataGrid
                                dataGridClassName={classes.grid}
                                columns={columns}
                                allowHeaderFilting={false}
                                allowUpdating={false}
                                allowDelete={false}
                                actionButtonsVisibility={false}
                                gridProps={{
                                    dataSource: dataSource,
                                    dataGridRef: gridRef,
                                    sorting: { mode: 'single' },
                                    onContentReady: handleOnContentReady,
                                    onSelectionChanged: handleOnSelectionChanged,
                                    onKeyDown: handleGridKeyDown
                                }}
                            />
                            <div className={classes.OrderRecallToogleContaner}>
                                <ToggleButton
                                    inputRef={lastToggleButtonRef}
                                    checked={defaultSuppliedQuantityToggle}
                                    disabled={disableAutoSupplyQuantity}
                                    onChange={handleToggleChange}
                                    label={'Default supplied quantity for available products'}
                                    value={'RecalculatePricing'}
                                    onKeyDown={handleToggleOnKeyDown}
                                />
                            </div>
                        </div>
                    }
                    actions={actions}
                    dialogActionsButtons={true}
                    dialogActionsShadow={false}
                />
            </HotKeys>
        </div>
    );
};

export default withStyles(OrderRecallStyles)(React.memo(OrderRecallDialog));
